import {useMemo} from "react";
import {useQueryClient} from "@tanstack/react-query";
import {useSnackbar} from "notistack";
import {useApiQuery} from "../api/hooks";
import apiClient from "../api/apiClient";
import {usePreloaderStore, useSmenaStore} from "../stores";

const ACTIVITY_BET_TYPE = {
    EVENT_WIN: 'EVENT_WIN',
    EVENT_TOP_N: 'EVENT_TOP_N',
    EVENT_EXACT_PLACE: 'EVENT_EXACT_PLACE'
};

const ACTIVITY_BET_TYPE_LABELS = {
    [ACTIVITY_BET_TYPE.EVENT_WIN]: 'Победа (1 место)',
    [ACTIVITY_BET_TYPE.EVENT_TOP_N]: 'Топ N',
    [ACTIVITY_BET_TYPE.EVENT_EXACT_PLACE]: 'Точное место'
};

export {ACTIVITY_BET_TYPE, ACTIVITY_BET_TYPE_LABELS};

export default function useBetEditState() {
    const queryClient = useQueryClient();
    const {enqueueSnackbar} = useSnackbar();
    const {show: showPreloader, hide: hidePreloader} = usePreloaderStore();
    const smenaId = useSmenaStore((state) => state.smenaId);

    const {data: gameData, isLoading: gameLoading, error: gameError} = useApiQuery(
        ['bet', 'game', smenaId],
        `/api/bet/admin/games/find-by-smena?smenaId=${smenaId}`,
        {refetchOnMount: 'always', enabled: !!smenaId}
    );

    const gameId = gameData?.id;

    const queryKeys = {
        game: ['bet', 'game', smenaId],
        teams: ['bet', 'teams', gameId],
        events: ['bet', 'events', smenaId],
        bets: ['bet', 'bets', smenaId],
        activityEvents: ['bet', 'activity-events', smenaId],
        activityBets: ['bet', 'activity-bets', smenaId],
        players: ['bet', 'players', smenaId],
        playerBets: ['bet', 'player-bets', smenaId]
    };

    const {data: teamsData, isLoading: teamsLoading} = useApiQuery(
        queryKeys.teams,
        `/api/bet/admin/teams/list-by-game?gameId=${gameId}`,
        {refetchOnMount: 'always', enabled: !!gameId}
    );

    const {data: eventsData, isLoading: eventsLoading} = useApiQuery(
        queryKeys.events,
        `/api/bet/admin/events/list-by-smena?smenaId=${smenaId}`,
        {refetchOnMount: 'always', enabled: !!smenaId && !!gameData}
    );

    const {data: betsData, isLoading: betsLoading} = useApiQuery(
        queryKeys.bets,
        `/api/bet/admin/bets/list-by-smena?smenaId=${smenaId}`,
        {refetchOnMount: 'always', enabled: !!smenaId && !!gameData}
    );

    const {data: activityEventsData, isLoading: activityEventsLoading} = useApiQuery(
        queryKeys.activityEvents,
        `/api/bet/admin/activity-events/list-all?smenaId=${smenaId}`,
        {refetchOnMount: 'always', enabled: !!smenaId && !!gameData}
    );

    const {data: playersData, isLoading: playersLoading} = useApiQuery(
        queryKeys.players,
        `/api/bet/admin/players/list-by-smena?smenaId=${smenaId}`,
        {refetchOnMount: 'always', enabled: !!smenaId && !!gameData}
    );

    const {data: activityBetsData, isLoading: activityBetsLoading} = useApiQuery(
        queryKeys.activityBets,
        `/api/bet/admin/activity-bets/list-by-smena?smenaId=${smenaId}`,
        {refetchOnMount: 'always', enabled: !!smenaId && !!gameData}
    );

    const {data: playerBetsData, isLoading: playerBetsLoading} = useApiQuery(
        queryKeys.playerBets,
        `/api/bet/admin/bets/by-users?smenaId=${smenaId}`,
        {refetchOnMount: 'always', enabled: !!smenaId && !!gameData}
    );

    const teams = useMemo(() => {
        if (!teamsData) return [];
        return [...teamsData].sort((a, b) => a.name.localeCompare(b.name));
    }, [teamsData]);

    const events = useMemo(() => {
        if (!eventsData) return [];
        return [...eventsData].sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
    }, [eventsData]);

    const currentEvents = useMemo(() => events.filter(e => !e.finished), [events]);
    const finishedEvents = useMemo(() => events.filter(e => e.finished), [events]);

    const bets = useMemo(() => betsData || [], [betsData]);

    const betsByEventId = useMemo(() => {
        const grouped = {};
        bets.forEach(bet => {
            if (!grouped[bet.eventId]) {
                grouped[bet.eventId] = [];
            }
            grouped[bet.eventId].push(bet);
        });
        return grouped;
    }, [bets]);

    const activityEvents = useMemo(() => {
        if (!activityEventsData) return [];
        return [...activityEventsData].sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
    }, [activityEventsData]);

    const currentActivityEvents = useMemo(() => activityEvents.filter(e => !e.finished), [activityEvents]);
    const finishedActivityEvents = useMemo(() => activityEvents.filter(e => e.finished), [activityEvents]);

    const activityBets = useMemo(() => activityBetsData || [], [activityBetsData]);

    const activityBetsByEventId = useMemo(() => {
        const grouped = {};
        activityBets.forEach(bet => {
            const eventId = bet.activityEventId;
            if (!grouped[eventId]) {
                grouped[eventId] = [];
            }
            grouped[eventId].push(bet);
        });
        return grouped;
    }, [activityBets]);

    const players = useMemo(() => {
        if (!playersData) return [];
        return [...playersData].sort((a, b) => b.balance - a.balance);
    }, [playersData]);

    const playerBets = useMemo(() => {
        if (!playerBetsData) return [];
        return [...playerBetsData].sort((a, b) => (b.totalStaked || 0) - (a.totalStaked || 0));
    }, [playerBetsData]);

    const invalidateGame = () => {
        queryClient.invalidateQueries({queryKey: queryKeys.game});
    };

    const invalidateTeams = () => {
        queryClient.invalidateQueries({queryKey: queryKeys.teams});
    };

    const invalidateEvents = () => {
        queryClient.invalidateQueries({queryKey: queryKeys.events});
    };

    const invalidateActivityEvents = () => {
        queryClient.invalidateQueries({queryKey: queryKeys.activityEvents});
    };

    const invalidatePlayers = () => {
        queryClient.invalidateQueries({queryKey: queryKeys.players});
    };

    const refreshTeams = async () => {
        if (!gameId) return;
        showPreloader();
        try {
            const response = await apiClient.post(`/api/bet/admin/teams/refresh?gameId=${gameId}`);
            invalidateTeams();
            enqueueSnackbar(response.data?.message || `Команды обновлены (добавлено: ${response.data?.createdTeams || 0})`, {variant: "success"});
        } catch (error) {
            enqueueSnackbar("Ошибка при обновлении команд", {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    const deleteTeam = async (id) => {
        showPreloader();
        try {
            await apiClient.delete(`/api/bet/admin/teams/delete?id=${id}`);
            invalidateTeams();
            enqueueSnackbar("Команда удалена из игры", {variant: "success"});
        } catch (error) {
            enqueueSnackbar(error.response?.data?.message || "Ошибка при удалении команды", {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    const createEvent = async (eventData) => {
        if (!gameId) return;
        showPreloader();
        try {
            await apiClient.post(`/api/bet/admin/events/create?gameId=${gameId}`, eventData);
            invalidateEvents();
            enqueueSnackbar("Событие создано", {variant: "success"});
        } catch (error) {
            enqueueSnackbar(error.response?.data?.message || "Ошибка при создании события", {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    const finishEvent = async (eventId, result, winTeamId) => {
        showPreloader();
        try {
            await apiClient.post(`/api/bet/admin/events/finish?id=${eventId}`, {
                result,
                winTeamId
            });
            invalidateEvents();
            enqueueSnackbar("Событие завершено, ставки обработаны", {variant: "success"});
        } catch (error) {
            enqueueSnackbar(error.response?.data?.message || "Ошибка при завершении события", {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    const updateEvent = async (eventId, eventData) => {
        showPreloader();
        try {
            await apiClient.patch(`/api/bet/admin/events/update?id=${eventId}`, eventData);
            invalidateEvents();
            invalidateBets();
            invalidatePlayers();
            queryClient.invalidateQueries({queryKey: queryKeys.playerBets});
            enqueueSnackbar("Событие обновлено", {variant: "success"});
        } catch (error) {
            enqueueSnackbar(error.response?.data?.message || "Ошибка при обновлении события", {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    const deleteEvent = async (eventId) => {
        showPreloader();
        try {
            await apiClient.delete(`/api/bet/admin/events/delete?id=${eventId}`);
            invalidateEvents();
            invalidateBets();
            invalidatePlayers();
            queryClient.invalidateQueries({queryKey: queryKeys.playerBets});
            enqueueSnackbar("Событие удалено", {variant: "success"});
        } catch (error) {
            enqueueSnackbar(error.response?.data?.message || "Ошибка при удалении события", {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    const syncActivityEvents = async () => {
        showPreloader();
        try {
            const response = await apiClient.post(`/api/bet/admin/activity-events/sync?smenaId=${smenaId}`);
            invalidateActivityEvents();
            enqueueSnackbar(`Мероприятия синхронизированы (добавлено: ${response.data?.synced || 0})`, {variant: "success"});
        } catch (error) {
            enqueueSnackbar(error.response?.data?.message || "Ошибка при синхронизации мероприятий", {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    const createActivityEvent = async (eventData) => {
        showPreloader();
        try {
            await apiClient.post(`/api/bet/admin/activity-events/create?smenaId=${smenaId}`, eventData);
            invalidateActivityEvents();
            enqueueSnackbar("Мероприятие создано", {variant: "success"});
        } catch (error) {
            enqueueSnackbar(error.response?.data?.message || "Ошибка при создании мероприятия", {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    const finishActivityEvent = async (eventId, results) => {
        showPreloader();
        try {
            await apiClient.post(`/api/bet/admin/activity-events/finish?id=${eventId}`, {results});
            invalidateActivityEvents();
            invalidatePlayers();
            enqueueSnackbar("Мероприятие завершено, ставки обработаны", {variant: "success"});
        } catch (error) {
            enqueueSnackbar(error.response?.data?.message || "Ошибка при завершении мероприятия", {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    const updateActivityEvent = async (eventId, eventData) => {
        showPreloader();
        try {
            await apiClient.patch(`/api/bet/admin/activity-events/update?id=${eventId}`, eventData);
            invalidateActivityEvents();
            invalidateActivityBets();
            queryClient.invalidateQueries({queryKey: queryKeys.playerBets});
            enqueueSnackbar("Мероприятие обновлено", {variant: "success"});
        } catch (error) {
            enqueueSnackbar(error.response?.data?.message || "Ошибка при обновлении мероприятия", {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    const deleteActivityEvent = async (eventId) => {
        showPreloader();
        try {
            await apiClient.delete(`/api/bet/admin/activity-events/delete?id=${eventId}`);
            invalidateActivityEvents();
            invalidateActivityBets();
            invalidatePlayers();
            queryClient.invalidateQueries({queryKey: queryKeys.playerBets});
            enqueueSnackbar("Мероприятие удалено", {variant: "success"});
        } catch (error) {
            enqueueSnackbar(error.response?.data?.message || "Ошибка при удалении мероприятия", {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    const addBalance = async (userId, amount) => {
        showPreloader();
        try {
            await apiClient.post(`/api/bet/admin/balance/add?smenaId=${smenaId}`, {userId, amount});
            invalidatePlayers();
            enqueueSnackbar(`Баланс пополнен на ${amount} монет`, {variant: "success"});
        } catch (error) {
            enqueueSnackbar(error.response?.data?.message || "Ошибка при пополнении баланса", {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    const getEventById = (eventId) => {
        return events.find(e => e.id === eventId);
    };

    const getTeamById = (teamId) => {
        return teams.find(t => t.id === teamId || t.teamId === teamId);
    };

    const getBetsByEventId = (eventId) => {
        return betsByEventId[eventId] || [];
    };

    const getActivityEventById = (eventId) => {
        return activityEvents.find(e => e.id === eventId);
    };

    const getActivityBetsByEventId = (eventId) => {
        return activityBetsByEventId[eventId] || [];
    };

    const invalidateBets = () => {
        queryClient.invalidateQueries({queryKey: queryKeys.bets});
    };

    const invalidateActivityBets = () => {
        queryClient.invalidateQueries({queryKey: queryKeys.activityBets});
    };

    return {
        gameData,
        gameError,
        gameId,
        smenaId,
        events,
        currentEvents,
        finishedEvents,
        teams,
        bets,
        betsByEventId,

        activityEvents,
        currentActivityEvents,
        finishedActivityEvents,
        activityBets,
        activityBetsByEventId,

        players,
        playerBets,

        isLoading: gameLoading || teamsLoading || eventsLoading || betsLoading || activityEventsLoading || activityBetsLoading,
        gameLoading,
        teamsLoading,
        eventsLoading,
        betsLoading,
        activityEventsLoading,
        activityBetsLoading,
        playersLoading,
        playerBetsLoading,

        refreshTeams,
        deleteTeam,
        createEvent,
        updateEvent,
        finishEvent,
        deleteEvent,

        syncActivityEvents,
        createActivityEvent,
        updateActivityEvent,
        finishActivityEvent,
        deleteActivityEvent,

        addBalance,

        getEventById,
        getTeamById,
        getBetsByEventId,
        getActivityEventById,
        getActivityBetsByEventId,

        invalidateGame,
        invalidateTeams,
        invalidateEvents,
        invalidateBets,
        invalidateActivityEvents,
        invalidateActivityBets,
        invalidatePlayers
    };
}
